import type { AppDispatch } from "../store/makeStore";
import { addRecipe } from "../store/slices/recipesSlice";
import { addIngredients } from "../store/slices/ingredientsSlice";
import { addMethodSteps } from "../store/slices/methodStepsSlice";
import { generateId } from "../utils/generateId";
import type { Recipe } from "../models/recipe";
import type { Ingredient } from "../models/ingredient";
import type { MethodStep } from "../models/method-step";

export interface DuplicateRecipeSource {
  recipe: Recipe;
  ingredients: Ingredient[];
  methodSteps: MethodStep[];
}

// Create an editable copy of a recipe — new ids for the recipe and every
// ingredient/step, title suffixed " (copy)". Shared by both apps. Returns the new
// recipe id so the caller can navigate to it.
//
// The image is intentionally NOT copied: images live at
// `${userId}/${recipeId}.jpg`, so reusing the URL would point the copy at the
// original's Storage object — and deleting either recipe would then remove the
// other's photo. The copy starts image-less; add one if wanted.
export function duplicateRecipe(
  dispatch: AppDispatch,
  { recipe, ingredients, methodSteps }: DuplicateRecipeSource,
): string {
  const newRecipeId = generateId();

  const copy: Recipe = {
    ...recipe,
    id: newRecipeId,
    title: `${recipe.title} (copy)`,
    image: "",
    localImageUri: undefined,
    imagePendingUpload: undefined,
    cachedImageUri: undefined,
    // A fresh copy hasn't been cooked or favourited; cooking history is keyed by
    // recipe id and isn't copied either.
    cooked: false,
    favourite: false,
    // Let the server stamp these (matches the create-new path).
    createdAt: undefined,
    updatedAt: undefined,
    deletedAt: undefined,
  };
  dispatch(addRecipe(copy));

  // New ids + recipeId; the slice setters re-stamp ordering + timestamps.
  const liveIngredients = ingredients
    .filter((i) => !i.deletedAt)
    .map((i) => ({
      ...i,
      id: generateId(),
      recipeId: newRecipeId,
      createdAt: undefined,
      updatedAt: undefined,
      deletedAt: undefined,
    }));
  if (liveIngredients.length) {
    dispatch(addIngredients({ recipeId: newRecipeId, ingredients: liveIngredients }));
  }

  const liveSteps = methodSteps
    .filter((s) => !s.deletedAt)
    .map((s) => ({
      ...s,
      id: generateId(),
      recipeId: newRecipeId,
      createdAt: undefined,
      updatedAt: undefined,
      deletedAt: undefined,
    }));
  if (liveSteps.length) {
    dispatch(addMethodSteps({ recipeId: newRecipeId, methodSteps: liveSteps }));
  }

  return newRecipeId;
}
